import React from "react";
import {
  FileText, Edit3, Send, UserPlus, UserMinus, Play, PauseCircle, CheckCircle, Lock, Circle,
  Clock,
} from "react-feather";
import SectionCard from "../../../components/common/SectionCard";
import { useGetTechnicalWorkActivityQuery } from "../technicalApi";
import { ACTIVITY_LABEL } from "../technicalStatusMaps";

// Work item History.
//
// THREE PRESENTATION BUGS ARE FIXED HERE, all reported by the user against the
// Flutter screen on 2026-09-08 and all caused by the same thing: half the
// columns this timeline is derived from are SQL `date`, not `datetime`.
//
// CreatedDate, PushedToTechnicalDate and Assignment.RemovedDate are real
// DATETIMEs. StartedDate, OnHoldDate, CompletedDate, ClosedDate and
// Assignment.AssignedDate are DATE-only. sp_app_technical_work_activity_list
// CASTs them all into one UNION, so the date-only ones arrive as midnight.
//
//  1. Never print a clock time for a date-only event - "5 Sep 2026, 12:00 AM"
//     is invented precision, and it made two unrelated events look
//     simultaneous. Same rule as the elapsed/turnaround badge already uses.
//  2. Never trust ActedDate alone for ordering. The proc orders by ActedDate
//     first, so a date-only Completed (00:00) sorts ABOVE a Logged at 00:10
//     the same day - which is exactly what the user saw. Sort by calendar DAY,
//     then by lifecycle rank. (The proc has its own SortRank but does not
//     return it, so it is re-derived here.)
//  3. Never split ONE action into two steps. For a Complaint or a Service
//     Visit there is no push step at all - the create proc sets
//     PushedToTechnicalDate = CreatedDate in the same statement, because those
//     go straight to the Technical Manager with no approval. Only an
//     Installation genuinely waits to be pushed. Collapsed on identical
//     actor+timestamp rather than on sourceType, so it stays right either way.
const ACTIVITY_META = {
  LOGGED: { icon: FileText, rank: 0, color: "text-secondary", hasTime: true },
  DESCRIPTION_EDITED: { icon: Edit3, rank: 1, color: "text-warning", hasTime: true },
  PUSHED_TO_TECHNICAL: { icon: Send, rank: 2, color: "text-primary", hasTime: true },
  TECHNICIAN_ASSIGNED: { icon: UserPlus, rank: 3, color: "text-info", hasTime: false },
  TECHNICIAN_REMOVED: { icon: UserMinus, rank: 4, color: "text-danger", hasTime: true },
  STARTED: { icon: Play, rank: 5, color: "text-info", hasTime: false },
  ON_HOLD: { icon: PauseCircle, rank: 6, color: "text-warning", hasTime: false },
  COMPLETED: { icon: CheckCircle, rank: 7, color: "text-success", hasTime: false },
  CLOSED: { icon: Lock, rank: 8, color: "text-success", hasTime: false },
};

const metaFor = (type) => ACTIVITY_META[type] || { icon: Circle, rank: 9, color: "text-muted", hasTime: false };

const dayKey = (iso) => (iso ? String(iso).slice(0, 10) : "");

const formatDay = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "-";

const formatWhen = (iso, hasTime) => {
  if (!iso) return "-";
  const day = formatDay(iso);
  if (!hasTime) return day;
  const time = new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `${day}, ${time}`;
};

// Names come from two different sources - ActedByName is an App_Users.FullName,
// the assigned technician in Detail is an Employee_Details name - so compare
// them normalised, and treat a match as a HINT (which is all it can be), never
// as identity.
const norm = (s) => (s || "").replace(/\s+/g, " ").trim().toUpperCase();

// The history shows people by first name only (2026-09-14, by instruction) -
// the user who acted and the technician assigned/removed alike. Display only:
// the folding below still matches on the full names. A leading title (Mr,
// Mrs, Ms, Dr) is skipped so it never stands in for the name.
const TITLES = new Set(["MR", "MRS", "MS", "MISS", "DR"]);
const firstName = (fullName) => {
  const parts = (fullName || "").trim().split(/\s+/).filter(Boolean);
  while (parts.length > 1 && TITLES.has(parts[0].replace(/\./g, "").toUpperCase())) parts.shift();
  return parts[0] || fullName || "";
};

// Entries whose Detail is a technician's name rather than remarks.
const DETAIL_IS_PERSON = new Set(["TECHNICIAN_ASSIGNED", "TECHNICIAN_REMOVED"]);

// Fold the rows that are really ONE action into the "Logged" entry.
//
// Two separate cases, both of which showed up as duplicate steps:
//
//  a) PUSHED_TO_TECHNICAL - for a Complaint or Service Visit the create proc
//     sets PushedToTechnicalDate = CreatedDate in the same statement (there is
//     no approval step for those two), so the timestamps are byte-identical.
//     It carries no information of its own, so it is simply dropped.
//
//  b) TECHNICIAN_ASSIGNED - LogServiceVisitAsync / LogComplaintAsync take the
//     technician list in the SAME request that creates the row
//     (AssignAtCreationIfRequestedAsync, with the same callerId), and AMC's
//     "Assign Technician" button goes through exactly that call. So one click
//     writes CreatedBy and AssignedBy as the same user at the same moment.
//     This row DOES carry information - who was assigned - so it is merged
//     into the Logged entry rather than dropped.
//
//     Matched on same actor + same CALENDAR DAY, not exact timestamp, because
//     AssignedDate is a SQL `date` (midnight) while CreatedDate is a
//     `datetime` - the two can never compare equal. The looser match can also
//     absorb an assignment made later the same day by the same person, which
//     is acceptable: the merged line only claims that person logged it and
//     assigned it that day, which stays true either way, and the technician's
//     name is preserved.
const foldCreationRows = (rows) => {
  const logged = rows.find((r) => r.activityType === "LOGGED");
  const assignedAtCreation = [];

  const kept = rows.filter((r) => {
    // SELF-ASSIGNMENT IS NOT A STEP, anywhere in the timeline. Direct
    // instruction: "I am logging into app and selecting service visit and
    // doing the service and log the details, so there is no assign myself as
    // technician here." Putting your own name on your own job is not a
    // dispatch decision - the system records it because the assignment table
    // needs a row, not because a person performed an action worth logging.
    // Nothing is lost: the Technicians card on the detail screen still names
    // who did the work.
    //
    // Checked BEFORE the merge below so a self-assignment can never survive
    // as an "Assigned to ..." line on the Logged entry either.
    if (r.activityType === "TECHNICIAN_ASSIGNED" && norm(r.detail) === norm(r.actedByName)) {
      return false;
    }

    if (!logged) return true;

    if (
      r.activityType === "PUSHED_TO_TECHNICAL" &&
      r.actedDate === logged.actedDate &&
      norm(r.actedByName) === norm(logged.actedByName)
    ) {
      return false;
    }

    // Assigning SOMEONE ELSE at creation stays - that is a real dispatch
    // decision - but it happened in the same click as the logging, so it is
    // merged into that entry rather than shown as a second step.
    if (
      r.activityType === "TECHNICIAN_ASSIGNED" &&
      dayKey(r.actedDate) === dayKey(logged.actedDate) &&
      norm(r.actedByName) === norm(logged.actedByName)
    ) {
      assignedAtCreation.push(r);
      return false;
    }
    return true;
  });

  return { rows: kept, assignedAtCreation };
};

const orderTimeline = (rows) =>
  [...rows].sort((a, b) => {
    const dayDiff = dayKey(a.actedDate).localeCompare(dayKey(b.actedDate));
    if (dayDiff !== 0) return dayDiff;
    return metaFor(a.activityType).rank - metaFor(b.activityType).rank;
  });

const ActivityTimeline = ({ recordGuid }) => {
  const { data = [], isLoading, isError, error } = useGetTechnicalWorkActivityQuery(recordGuid);

  if (isLoading) {
    return (
      <SectionCard icon={Clock} title="History">
        <p className="mb-0 text-muted">Loading history...</p>
      </SectionCard>
    );
  }

  if (isError) {
    return (
      <SectionCard icon={Clock} title="History">
        <div className="alert alert-danger mb-0 py-2 px-3">
          <small>Could not load the history ({error?.message || "server error"}).</small>
        </div>
      </SectionCard>
    );
  }

  const { rows: folded, assignedAtCreation } = foldCreationRows(data);
  const rows = orderTimeline(folded);
  const assignedAtCreationNames = assignedAtCreation.map((r) => r.detail).filter(Boolean).map(firstName);

  return (
    <SectionCard icon={Clock} title="History">
      <>
        {rows.length === 0 ? (
          <p className="text-muted mb-0">Nothing recorded yet.</p>
        ) : (
          <ul className="list-unstyled mb-0 technical-timeline">
            {rows.map((row, index) => {
              const meta = metaFor(row.activityType);
              const Icon = meta.icon;
              return (
                <li key={`${row.activityType}-${row.actedDate}-${index}`} className="d-flex mb-3">
                  <div className="me-3 d-flex flex-column align-items-center">
                    <Icon size={16} className={meta.color} />
                    {index < rows.length - 1 && (
                      <span className="flex-grow-1 border-start mt-1" style={{ minHeight: 18 }} />
                    )}
                  </div>
                  <div className="flex-grow-1">
                    <div>
                      <strong className={meta.color}>
                        {row.activityType === "LOGGED" && assignedAtCreationNames.length > 0
                          ? "Logged & technician assigned"
                          : ACTIVITY_LABEL[row.activityType] || row.activityType}
                      </strong>
                      {row.actedByName && <span className="text-muted"> by {firstName(row.actedByName)}</span>}
                    </div>
                    <div className="text-muted small">{formatWhen(row.actedDate, meta.hasTime)}</div>
                    {row.activityType === "LOGGED" && assignedAtCreationNames.length > 0 && (
                      <div className="small mt-1">Assigned to {assignedAtCreationNames.join(", ")}</div>
                    )}
                    {row.detail && (
                      <div className="small mt-1">
                        {DETAIL_IS_PERSON.has(row.activityType) ? firstName(row.detail) : row.detail}
                      </div>
                    )}
                    {/* Only a description edit carries old/new text - the old
                        wording is struck through under the new one so nothing
                        is silently lost. */}
                    {row.activityType === "DESCRIPTION_EDITED" ? (
                      <div className="small mt-1">
                        {row.newValue && <div>{row.newValue}</div>}
                        {row.oldValue && <div className="text-muted text-decoration-line-through">{row.oldValue}</div>}
                      </div>
                    ) : (
                      row.newValue && <div className="small mt-1">{row.newValue}</div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </>
    </SectionCard>
  );
};

export default ActivityTimeline;
